import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { fetchGrades } from '../mishtameretPortalApi';
import { EXAM_FRAMES } from '../nav';
import { formatHebrewDate } from '../../../utils/hebrewDate';

const ALL = 'all';

/**
 * היסטוריית ציונים — סיכום לכל מסגרת ורשימת כל המבחנים.
 * הסדר מגיע מהשרת: קודם מבחני המערכת (האחרון ראשון), אחריהם ההיסטוריה לפי מספר מבחן.
 */
const ScoresScreen = () => {
  const { theme } = useOutletContext();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [frame, setFrame] = useState(ALL);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchGrades();
        if (!cancelled) setGrades(data.grades || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת ציונים');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const summaries = useMemo(() => EXAM_FRAMES
    .map((f) => {
      const list = grades.filter((g) => g.frame === f.id);
      if (!list.length) return null;
      const avg = Math.round(list.reduce((s, g) => s + (g.score || 0), 0) / list.length);
      return { ...f, count: list.length, avg };
    })
    .filter(Boolean), [grades]);

  const visible = frame === ALL ? grades : grades.filter((g) => g.frame === frame);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  if (!grades.length) {
    return (
      <Box>
        {error ? <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography> : null}
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.radius}px`,
            border: `1px dashed ${theme.border}`,
            color: theme.textMuted,
            textAlign: 'center',
          }}
        >
          עדיין אין ציונים להצגה
        </Box>
      </Box>
    );
  }

  const chip = (id, label) => {
    const active = frame === id;
    return (
      <ButtonBase
        key={id}
        onClick={() => setFrame(id)}
        aria-pressed={active}
        sx={{
          minHeight: 36,
          px: 1.75,
          borderRadius: 999,
          border: `1px solid ${active ? theme.accent : theme.border}`,
          bgcolor: active ? theme.accent : theme.surface,
          color: active ? theme.accentContrast : theme.text,
          fontWeight: 700,
          fontSize: '0.9rem',
          '&:hover': { bgcolor: active ? theme.accent : theme.surfaceHover },
        }}
      >
        {label}
      </ButtonBase>
    );
  };

  return (
    <Box>
      {error ? <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography> : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: `repeat(${summaries.length}, 1fr)` },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {summaries.map((s) => (
          <Box
            key={s.id}
            sx={{
              p: 2,
              borderRadius: `${theme.radius}px`,
              border: `1px solid ${theme.borderSoft}`,
              bgcolor: theme.surface,
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>{s.label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: theme.fontDisplay,
                  fontWeight: 700,
                  fontSize: '1.6rem',
                  color: theme.accent,
                  lineHeight: 1.1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {s.avg}
              </Typography>
              <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>ציון ממוצע</Typography>
            </Box>
            <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
              {s.count === 1 ? 'מבחן אחד' : `${s.count} מבחנים`}
            </Typography>
          </Box>
        ))}
      </Box>

      {summaries.length > 1 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {chip(ALL, 'הכל')}
          {summaries.map((s) => chip(s.id, s.label))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {visible.map((g) => (
          <Box
            key={g.id}
            sx={{
              p: 1.75,
              borderRadius: `${theme.radius}px`,
              border: `1px solid ${theme.borderSoft}`,
              bgcolor: theme.surface,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{g.examTitle}</Typography>
              <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
                {[
                  frame === ALL ? g.frameLabel : null,
                  g.materialLabel,
                  formatHebrewDate(g.finishedAt),
                ].filter(Boolean).join(' · ')}
              </Typography>
              {g.totalQuestions > 0 && (
                <Typography sx={{ color: theme.textMuted, fontSize: '0.8rem', mt: 0.25 }}>
                  {g.correctCount}/{g.totalQuestions} נכונות
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
              <Typography
                sx={{
                  fontFamily: theme.fontDisplay,
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: theme.accent,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {g.score}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ScoresScreen;
